import BaseDialog from "@/components/Modals/BaseDialog";
import { useRouter } from "next/navigation";
import { ArrowUpRight } from "lucide-react";
import { Button } from "../shadcn/button";
import { Badge } from "../shadcn/badge";
import { Banner } from "../Banner";
import { parseUtcDate } from "@/utils/generic";
import { usePermissions } from "@/hooks/usePermissions";
import { ORG_PERMISSIONS } from "@/constants/permissions";
import {
  AI_OPERATIONS_LABEL,
  getMetricLabel,
} from "@/constants/billingConstraints";
import { useBillingCurrency } from "@/hooks/useBillingCurrency";
import {
  BillingLimitParams,
  BillingLimitScope,
} from "@/utils/billingModalController";

const PRIMARY_METRIC_LABEL: Record<string, string> = {
  aioperations: AI_OPERATIONS_LABEL,
  tokens: "AI model usage",
};

const SCOPE_LABEL: Record<BillingLimitScope, string> = {
  installation: "Installation limit",
  organization: "Organization limit",
  workspace: "Workspace limit",
};

function amount(
  metric: string | null | undefined,
  value: number,
  currency: ReturnType<typeof useBillingCurrency>
): string {
  // Token costs arrive in USD; shown in the currency the Billing page uses.
  return metric === "tokens"
    ? currency.format(value, 2)
    : value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

export function LimitHitModal({
  isOpen,
  onClose,
  params,
}: {
  isOpen: boolean;
  onClose: () => void;
  params: BillingLimitParams;
}) {
  const router = useRouter();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(ORG_PERMISSIONS.ORG.BILLING, "tenant");
  const currency = useBillingCurrency();
  const isLimit = !!params.limit_id;
  const internal = isLimit && !!params.is_internal_limit;
  // How the limit is set up is for those who can change it; everyone else is
  // only told that one was reached.
  const showDetails = isLimit && canManage;
  const metricLabel = params.metric
    ? (PRIMARY_METRIC_LABEL[params.metric] ?? getMetricLabel(params.metric))
    : null;
  // AI Operations is a product name; every other metric reads in lower case mid-sentence.
  const metricInSentence =
    metricLabel === AI_OPERATIONS_LABEL
      ? metricLabel
      : metricLabel?.toLowerCase();
  const scopeLabel = params.scope ? SCOPE_LABEL[params.scope] : "Usage limit";
  const capacity = params.capacity ?? null;
  const limit =
    capacity == null
      ? null
      : params.payg_capacity == null
        ? capacity
        : capacity + params.payg_capacity;
  const nothingIncluded = limit === 0;
  const resetDate = params.next_reset_at
    ? parseUtcDate(params.next_reset_at).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      })
    : null;
  const filter = Object.entries(params.filter ?? {})
    .map(([key, value]) => `${key}: ${value}`)
    .join(" · ");
  const manage = () => {
    onClose();
    router.push(
      internal
        ? "/settings/administration/billing?tab=limits"
        : "/settings/administration/billing"
    );
  };
  return (
    <BaseDialog
      title={
        !isLimit
          ? "Billing action required"
          : !showDetails
            ? "You've reached a usage limit"
            : nothingIncluded && metricLabel
              ? `${metricLabel} is not included in your plan`
              : metricLabel
                ? `You've reached your ${metricLabel} limit`
                : "You've reached a usage limit"
      }
      description={
        !isLimit
          ? "A billing issue is preventing this action. Contact your billing administrator to resolve it."
          : !canManage
            ? "Contact an administrator of your organization to review this limit."
            : internal
              ? "Wait for the limit to reset, or adjust it under Usage controls on the Billing page."
              : "Wait for the limit to reset, or contact your billing administrator to request a higher one."
      }
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="xl"
    >
      <div className="flex flex-col gap-6">
        {showDetails && params.metric && (
          <div className="rounded-lg border bg-gray-50 p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground">
                {nothingIncluded ? "Allowance" : "Current usage"}
              </span>
              <Badge variant="outline">
                {internal ? `${scopeLabel} · your usage control` : scopeLabel}
              </Badge>
            </div>
            {nothingIncluded ? (
              <p className="text-sm">
                No {metricInSentence} are included
                {params.scope ? ` for this ${params.scope}` : ""}
                {internal ? "" : " in the current plan"}.
              </p>
            ) : (
              <>
                <div className="mb-2 flex items-end justify-between text-sm tabular-nums">
                  <span>
                    {amount(params.metric, params.value ?? 0, currency)}{" "}
                    <span className="text-muted-foreground">
                      {metricLabel} used
                      {(params.reserved ?? 0) > 0 &&
                        ` · ${amount(params.metric, params.reserved ?? 0, currency)} held by unfinished work`}
                    </span>
                  </span>
                  <span className="text-muted-foreground">
                    {limit == null
                      ? capacity == null
                        ? ""
                        : `of ${amount(params.metric, capacity, currency)} included · overage budget used up`
                      : `of ${amount(params.metric, limit, currency)}`}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-2 w-full rounded-full bg-destructive" />
                </div>
              </>
            )}
            {filter && (
              <p className="mt-2 text-xs text-muted-foreground">
                Applies to {filter}
              </p>
            )}
          </div>
        )}
        {showDetails && resetDate && (
          <Banner style="info" label={`This limit resets on ${resetDate}.`} />
        )}
        <div className="flex items-center gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            {isLimit ? "I'll wait" : "Close"}
          </Button>
          {canManage && (
            <Button className="flex-1" onClick={manage}>
              <ArrowUpRight size="1rem" />
              {internal ? "Manage usage controls" : "View billing"}
            </Button>
          )}
        </div>
      </div>
    </BaseDialog>
  );
}
